import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { dashboardService } from '@/services/dashboard.service';
import {
  StatsCard,
  LeadsChart,
  VisitsChart,
  CommissionChart,
  LeadSourcePieChart,
  ActivityFeed,
  QuickActions,
  WalletSummary,
  UpcomingVisits,
} from '@/components/dashboard';
import {
  DashboardStats,
  LeadsChartData,
  VisitsChartData,
  CommissionChartData,
  LeadSourceData,
  ActivityItem,
  TimePeriod,
} from '@/types/dashboard.types';

/**
 * Format amount in Indian currency format
 */
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Calculate percentage change between two values
 */
const calculateChange = (current: number, previous: number): { value: string; direction: 'up' | 'down' | 'neutral' } => {
  if (previous === 0) {
    return current > 0
      ? { value: '+100%', direction: 'up' }
      : { value: '0%', direction: 'neutral' };
  }

  const change = ((current - previous) / previous) * 100;
  const rounded = Math.abs(Math.round(change));

  if (change > 0) {
    return { value: `+${rounded}%`, direction: 'up' };
  } else if (change < 0) {
    return { value: `-${rounded}%`, direction: 'down' };
  }
  return { value: '0%', direction: 'neutral' };
};

// Mock data for demonstration (will be replaced by API calls)
const mockStats: DashboardStats = {
  leads: {
    total_this_month: 42,
    total_last_month: 38,
    qleads_this_month: 15,
    conversion_rate: 24,
  },
  visits: {
    total_this_month: 28,
    completed_this_month: 22,
    scheduled_upcoming: 8,
    no_shows_this_month: 3,
  },
  wallet: {
    balance: 12500,
    commission_this_month: 45000,
    pending_topups: 0,
  },
  referrals: {
    pending_received: 3,
    pending_sent: 2,
    completed_this_month: 5,
  },
};

const mockLeadsChart: LeadsChartData[] = [
  { period: '2024-01-01', label: 'Week 1', leads: 8, qleads: 3 },
  { period: '2024-01-08', label: 'Week 2', leads: 12, qleads: 5 },
  { period: '2024-01-15', label: 'Week 3', leads: 10, qleads: 4 },
  { period: '2024-01-22', label: 'Week 4', leads: 12, qleads: 3 },
];

const mockVisitsChart: VisitsChartData[] = [
  { period: '2024-01-01', label: 'Week 1', scheduled: 6, completed: 5, no_shows: 1 },
  { period: '2024-01-08', label: 'Week 2', scheduled: 8, completed: 7, no_shows: 1 },
  { period: '2024-01-15', label: 'Week 3', scheduled: 7, completed: 5, no_shows: 1 },
  { period: '2024-01-22', label: 'Week 4', scheduled: 7, completed: 5, no_shows: 0 },
];

const mockCommissionChart: CommissionChartData[] = [
  { period: '2024-01-01', label: 'Week 1', amount: 12000 },
  { period: '2024-01-08', label: 'Week 2', amount: 8500 },
  { period: '2024-01-15', label: 'Week 3', amount: 15000 },
  { period: '2024-01-22', label: 'Week 4', amount: 9500 },
];

const mockLeadSources: LeadSourceData[] = [
  { source: 'WEBSITE', count: 15, percentage: 35.7 },
  { source: 'INSTAGRAM', count: 8, percentage: 19.0 },
  { source: 'META', count: 6, percentage: 14.3 },
  { source: 'GOOGLE', count: 5, percentage: 11.9 },
  { source: 'REFERRAL', count: 4, percentage: 9.5 },
  { source: 'MANUAL', count: 4, percentage: 9.5 },
];

const mockActivity: ActivityItem[] = [
  {
    id: '1',
    type: 'lead_created',
    title: 'New lead added',
    description: 'Rahul Sharma - Website inquiry',
    metadata: { lead_id: '123', lead_name: 'Rahul Sharma' },
    created_at: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
  },
  {
    id: '2',
    type: 'visit_completed',
    title: 'Visit completed',
    description: 'Priya Patel visited Sky Heights',
    metadata: { visit_id: '456', lead_name: 'Priya Patel' },
    created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '3',
    type: 'referral_received',
    title: 'New referral received',
    description: 'From Amit Kumar - 3BHK requirement',
    metadata: { referral_id: '789' },
    created_at: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '4',
    type: 'commission_earned',
    title: 'Commission earned',
    description: 'Rs. 25,000 from Green Valley deal',
    metadata: { amount: 25000 },
    created_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '5',
    type: 'visit_scheduled',
    title: 'Visit scheduled',
    description: 'Ananya Singh - Tomorrow at 10:00 AM',
    metadata: { visit_id: '101', lead_name: 'Ananya Singh' },
    created_at: new Date(Date.now() - 26 * 60 * 60 * 1000).toISOString(),
  },
];

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [leadsChartData, setLeadsChartData] = useState<LeadsChartData[]>([]);
  const [visitsChartData, setVisitsChartData] = useState<VisitsChartData[]>([]);
  const [commissionChartData, setCommissionChartData] = useState<CommissionChartData[]>([]);
  const [leadSourcesData, setLeadSourcesData] = useState<LeadSourceData[]>([]);
  const [activityData, setActivityData] = useState<ActivityItem[]>([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);

        // Try to fetch from API, fall back to mock data
        try {
          const response = await dashboardService.getDashboard('month');
          setStats(response.data.stats);
          setLeadsChartData(response.data.leads_chart);
          setVisitsChartData(response.data.visits_chart);
          setCommissionChartData(response.data.commission_chart);
          setLeadSourcesData(response.data.lead_sources);
          setActivityData(response.data.recent_activity);
        } catch (apiError) {
          // Use mock data if API is not available
          console.log('Using mock data for dashboard');
          setStats(mockStats);
          setLeadsChartData(mockLeadsChart);
          setVisitsChartData(mockVisitsChart);
          setCommissionChartData(mockCommissionChart);
          setLeadSourcesData(mockLeadSources);
          setActivityData(mockActivity);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const handleLeadsChartPeriodChange = async (period: TimePeriod) => {
    try {
      const response = await dashboardService.getLeadsChart(period);
      setLeadsChartData(response.data);
    } catch (error) {
      console.error('Error fetching leads chart:', error);
    }
  };

  const handleVisitsChartPeriodChange = async (period: TimePeriod) => {
    try {
      const response = await dashboardService.getVisitsChart(period);
      setVisitsChartData(response.data);
    } catch (error) {
      console.error('Error fetching visits chart:', error);
    }
  };

  const handleCommissionChartPeriodChange = async (period: TimePeriod) => {
    try {
      const response = await dashboardService.getCommissionChart(period);
      setCommissionChartData(response.data);
    } catch (error) {
      console.error('Error fetching commission chart:', error);
    }
  };

  // Calculate trend for stats
  const leadsTrend = stats
    ? calculateChange(stats.leads.total_this_month, stats.leads.total_last_month)
    : { value: '0%', direction: 'neutral' as const };

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl p-6 text-white">
        <h1 className="text-2xl sm:text-3xl font-bold">
          Welcome back, {user?.full_name?.split(' ')[0] || 'Broker'}!
        </h1>
        <p className="mt-2 text-primary-100">
          Here is your business overview for this month.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatsCard
          title="Total Leads"
          value={stats?.leads.total_this_month ?? 0}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
          }
          trend={leadsTrend.direction}
          trendValue={leadsTrend.value}
          color="primary"
          isLoading={isLoading}
        />

        <StatsCard
          title="Total Visits"
          value={stats?.visits.total_this_month ?? 0}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          }
          color="info"
          isLoading={isLoading}
        />

        <StatsCard
          title="Wallet Balance"
          value={formatCurrency(stats?.wallet.balance ?? 0)}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
              />
            </svg>
          }
          color={(stats?.wallet.balance ?? 0) < 50 ? 'warning' : 'success'}
          isLoading={isLoading}
        />

        <StatsCard
          title="Pending Referrals"
          value={stats?.referrals.pending_received ?? 0}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          }
          color="warning"
          isLoading={isLoading}
        />

        <StatsCard
          title="Commission (MTD)"
          value={formatCurrency(stats?.wallet.commission_this_month ?? 0)}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          }
          color="success"
          isLoading={isLoading}
        />

        <StatsCard
          title="Conversion Rate"
          value={`${stats?.leads.conversion_rate ?? 0}%`}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          }
          color="primary"
          isLoading={isLoading}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <LeadsChart
          data={leadsChartData}
          isLoading={isLoading}
          onPeriodChange={handleLeadsChartPeriodChange}
        />
        <VisitsChart
          data={visitsChartData}
          isLoading={isLoading}
          onPeriodChange={handleVisitsChartPeriodChange}
        />
      </div>

      {/* Second Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <CommissionChart
            data={commissionChartData}
            isLoading={isLoading}
            onPeriodChange={handleCommissionChartPeriodChange}
          />
        </div>
        <LeadSourcePieChart data={leadSourcesData} isLoading={isLoading} />
      </div>

      {/* Widgets Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Activity Feed - Takes 2 columns */}
        <div className="lg:col-span-2">
          <ActivityFeed activities={activityData} isLoading={isLoading} />
        </div>

        {/* Quick Actions */}
        <QuickActions />
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Visits */}
        <UpcomingVisits />

        {/* Wallet Summary */}
        <WalletSummary
          balance={stats?.wallet.balance}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};

export default Dashboard;
